import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { useUser } from "@/contexts/UserContext";
import { useFileManager } from "@/hooks/useFileManager";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CompactFileSelector } from "@/components/uploader/CompactFileSelector";
import { Badge } from "@/components/ui/badge";
import { Camera, Upload, ArrowRight, History, Eye, Download, Trash2, FileImage, CheckCircle, Clock, Users, Calendar, MapPin } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { api } from "@/services/api";
import { useEventInfo, usePhotographerEvents, usePhotographerStats, useUploadHistory } from "../hooks/useUploader";
import type { Event } from '@/types/schema';
import { PhotoCacheService } from "@/services/photoCacheService";

export default function Dashboard() {
  const { user, isLoading } = useUser();
  const { selectedFiles, setSelectedFiles } = useFileManager();
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const [eventId, setEventId] = useState<string | null>(null);

  // Capturar eventId da query string
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const eventIdParam = params.get('eventId');
    if (eventIdParam) {
      setEventId(eventIdParam);
      console.log('📋 EventId capturado da URL:', eventIdParam);
    }
  }, []);

  // Usar hooks do módulo uploader
  const { data: eventInfo, isLoading: loadingEvent, error: eventError } = useEventInfo(eventId || '');
  const { data: photographerEvents } = usePhotographerEvents(user?.id || '');
  const { data: photographerStats } = usePhotographerStats(user?.id || '');
  const { data: uploadHistory } = useUploadHistory(user?.id || '', 5);

  // Debug logs
  useEffect(() => {
    console.log('🔍 Debug Dashboard:', {
      eventId,
      eventInfo,
      loadingEvent,
      eventError
    });
  }, [eventId, eventInfo, loadingEvent, eventError]);

  // Mostrar erro se houver problema ao carregar evento
  useEffect(() => {
    if (eventError && eventId) {
      toast({
        title: 'Erro ao carregar evento',
        description: 'Não foi possível carregar as informações do evento.',
        variant: 'destructive',
      });
    }
  }, [eventError, eventId, toast]);

  // Redirecionar para login se não estiver autenticado
  useEffect(() => {
    if (!isLoading && !user) {
      setLocation('/login');
    }
  }, [user, isLoading, setLocation]);

  // Estados para dados reais
  const [stats, setStats] = useState({
    totalBatches: 0,
    totalPhotos: 0,
    photosToday: 0
  });
  const [batches, setBatches] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Buscar dados reais do dashboard
  const fetchDashboardData = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);

      // Buscar estatísticas do fotógrafo
      const statsResponse = await api.get(`/photo-batches/photographer/${user.id}/stats`);
      const statsPayload = statsResponse?.data;
      if (statsPayload?.success && statsPayload.data) {
        setStats(statsPayload.data);
      } else if (statsPayload && !('success' in statsPayload)) {
        // Caso o backend retorne o objeto diretamente
        setStats(statsPayload);
      }

      // Buscar lotes recentes do fotógrafo
      const batchesResponse = await api.get(`/photo-batches/photographer/${user.id}?limit=5`);
      const batchesPayload = batchesResponse?.data;
      let list: any[] = [];
      if (batchesPayload?.success && Array.isArray(batchesPayload.data?.batches)) {
        list = batchesPayload.data.batches;
      } else if (Array.isArray(batchesPayload?.batches)) {
        list = batchesPayload.batches;
      } else if (Array.isArray(batchesPayload)) {
        // Caso o backend retorne diretamente um array
        list = batchesPayload;
      }


      setBatches(list || []);
    } catch (error) {
      console.error('Erro ao buscar dados do dashboard:', error);
      toast({
        title: "Erro ao carregar dados",
        description: "Não foi possível carregar os dados do dashboard.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Buscar dados quando o usuário estiver disponível
  useEffect(() => {
    if (user?.id) {
      fetchDashboardData();
    }
  }, [user?.id]);


  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground text-lg font-medium">Carregando dashboard...</p>
          <p className="text-muted-foreground text-sm mt-2">Verificando informações do fotógrafo</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null; // useEffect já vai redirecionar
  }

  const handleFilesSelected = (files: File[]) => {
    setSelectedFiles(files);
    toast({
      title: "Arquivos selecionados",
      description: `${files.length} arquivo(s) selecionado(s) para processamento.`,
    });
  };

  const handleProcessPhotos = () => {
    if (selectedFiles.length === 0) {
      toast({
        title: "Nenhum arquivo selecionado",
        description: "Selecione pelo menos um arquivo para processar.",
        variant: "destructive",
      });
      return;
    }

    if (!user?.id) {
      toast({
        title: "Usuário não autenticado",
        description: "Você precisa estar logado para processar fotos.",
        variant: "destructive",
      });
      return;
    }

    // Limpar cache existente e criar novo cache com arquivos selecionados
    PhotoCacheService.clearCache();
    PhotoCacheService.createInitialCache(selectedFiles, user.id);

    // Se houver eventId, setar no cache
    if (eventId) {
      PhotoCacheService.setEventId(eventId);
      console.log('📋 EventId definido no cache:', eventId);
    }

    // Redirecionar para PhotoProcessor
    setLocation('/uploader/photo-processor');
  };

  const handleViewBatchDetails = (batchId: string) => {
    setLocation(`/uploader/photos/${batchId}`);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-100 text-green-800 border-green-200">Concluído</Badge>;
      case 'processing':
        return <Badge className="bg-blue-100 text-blue-800 border-blue-200">Processando</Badge>;
      case 'failed':
        return <Badge className="bg-red-100 text-red-800 border-red-200">Falhou</Badge>;
      default:
        return <Badge variant="secondary">Desconhecido</Badge>;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="w-full px-6 py-8">

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-400 via-pink-400 to-purple-400 bg-clip-text text-transparent mb-2">
            Dashboard FotUp
          </h1>
          <p className="text-muted-foreground">
            Gerencie suas fotos e visualize o histórico de processamento
          </p>
        </div>

        <div className="mb-8">

          {/* Card de Informações do Evento */}
          {eventId && (
            <Card className="mb-8 border-0 bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-950 dark:to-orange-900">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">


                    {loadingEvent ? (
                      <div className="flex items-center space-x-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-500"></div>
                        <span className="text-sm text-orange-600">Carregando informações...</span>
                      </div>
                    ) : eventInfo ? (
                      <div className="space-y-3">
                        <div className="flex items-center space-x-2">
                          <h4 className="font-semibold text-orange-900 dark:text-orange-100 text-xl">
                            {eventInfo.title}
                          </h4>
                          {eventInfo.status && (
                            <Badge variant="outline" className="bg-orange-100 text-orange-800 border-orange-300">
                              {eventInfo.status}
                            </Badge>
                          )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {eventInfo.location && (
                            <div className="flex items-center text-sm text-orange-700 dark:text-orange-300">
                              <MapPin className="h-4 w-4 mr-2" />
                              <span className="font-medium">Local:</span>
                              <span className="ml-1">{eventInfo.location}</span>
                            </div>
                          )}


                        </div>

                        {eventInfo.description && (
                          <div className="mt-3">
                            <p className="text-sm text-orange-700 dark:text-orange-300">
                              <span className="font-medium">Descrição:</span> {eventInfo.description}
                            </p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-sm text-orange-600 dark:text-orange-400">
                        ⚠️ Evento não encontrado ou não foi possível carregar as informações
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

        </div>



        {/* Cards de Estatísticas */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="border-0 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-600 dark:text-blue-400">Total de Fotos</p>
                  <p className="text-3xl font-bold text-blue-900 dark:text-blue-100">{stats.totalPhotos}</p>
                </div>
                <div className="p-3 bg-blue-500 rounded-full">
                  <FileImage className="h-6 w-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 bg-gradient-to-br from-green-50 to-green-100 dark:from-green-950 dark:to-green-900">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-600 dark:text-green-400">Fotos Hoje</p>
                  <p className="text-3xl font-bold text-green-900 dark:text-green-100">{stats.photosToday}</p>
                </div>
                <div className="p-3 bg-green-500 rounded-full">
                  <CheckCircle className="h-6 w-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-950 dark:to-purple-900">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-purple-600 dark:text-purple-400">Total de Lotes</p>
                  <p className="text-3xl font-bold text-purple-900 dark:text-purple-100">{stats.totalBatches}</p>
                </div>
                <div className="p-3 bg-purple-500 rounded-full">
                  <History className="h-6 w-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>



        </div>

        {/* Seção de Upload - Compacta */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <CompactFileSelector
                onFilesSelected={handleFilesSelected}
                selectedFiles={selectedFiles}
              />
            </div>

            {selectedFiles.length > 0 && (
              <Button
                onClick={handleProcessPhotos}
                className="ml-6 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 shadow-lg shadow-purple-500/25"
              >
                <Upload className="w-4 h-4 mr-2" />
                Iniciar Processamento
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </div>

        {/* Tabela de Histórico */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <History className="h-5 w-5" />
              <span>Histórico de Processamento</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
                <p className="text-muted-foreground text-sm">Carregando lotes...</p>
                <p className="text-muted-foreground text-xs mt-1">Buscando histórico de processamento</p>
              </div>
            ) : (batches?.length ?? 0) === 0 ? (
              <div className="text-center py-8">
                <History className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Nenhum lote encontrado.</p>
                <p className="text-muted-foreground text-sm mt-1">Faça upload de fotos para ver o histórico aqui.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">ID</th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">Descrição</th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">Data/Hora</th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">Qtd Arquivos</th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(Array.isArray(batches) ? batches : []).map((batch, index) => (
                      <tr key={batch.id} className={`border-b border-border hover:bg-muted/50 transition-colors ${index % 2 === 0 ? 'bg-background' : 'bg-muted/20'}`}>
                        <td className="py-2 px-4 align-middle">
                          <div className="font-mono text-sm font-medium text-primary">
                            #{String(index + 1).padStart(3, '0')}
                          </div>
                        </td>
                        <td className="py-2 px-4 align-middle">
                          <div className="font-medium text-sm text-foreground">
                            {batch.batch_name || 'Sem nome'}
                          </div>
                        </td>
                        <td className="py-2 px-4 align-middle">
                          <div className="text-sm font-medium">
                            {new Date(batch.created_at).toLocaleDateString('pt-BR')} {new Date(batch.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </td>
                        <td className="py-2 px-4 align-middle">
                          <div className="flex items-center space-x-2">
                            <FileImage className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium">{batch.processed_files || batch.total_files || 0}</span>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center space-x-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewBatchDetails(batch.id)}
                              className="h-8 w-8 p-0 hover:bg-blue-100 dark:hover:bg-blue-900"
                              title="Ver detalhes do lote"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>


    </div>
  );
}
